import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./mcs.scss";
import { deriveEntitlementState } from "./McsTable";
import RedirectToView from "components/icons/RedirectToView";
import { createLicenseTypes } from "components/licenses/common";
import { ENTITLEMENT_LICENSES_ROUTE } from "components/router/Routes";
import StatusBadge from "components/status-badge/StatusBadge";
import StaticTable from "components/support/api-guide/StaticTable";
import Heading from "components/typography/heading/Heading";
import { McsTableData } from "domain/mc";
import buttons from "styles/buttons.scss";
import { formatDateWithoutTime } from "utils/format";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    mcsTableData: McsTableData;
    onClick: () => void;
}

function derivedSynchronizedCountValue(table: McsTableData, defaultValue: string): string {
    const synchronizedCount = table.reportSynchronizedCount;
    if (synchronizedCount == null) {
        return defaultValue;
    }
    const syncCountPart = `${synchronizedCount}`;
    const totalCount = table.reportCount;
    if (totalCount <= 0) {
        return syncCountPart;
    }
    const percent = Math.round((synchronizedCount / totalCount) * 100);
    return syncCountPart + ` (${percent}%)`;
}

const McSyncInformationView: React.FC<Props> = ({ mcsTableData, onClick }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const blanccoProducts = createLicenseTypes(true, true);
    const synchronizedCountDisplay = derivedSynchronizedCountValue(mcsTableData, t("Common.na"));
    return (
        <div data-testid={testIds.workArea.mcs.informationDialog.itself}>
            <div className={style.syncDataSection}>
                <div className={style.syncInfo}>
                    <div className={style.label}>{t("Mcs.onPremBmpInfoModal.name")}</div>
                    <div className={style.breakpointXxl} data-testid={testIds.workArea.mcs.informationDialog.name}>
                        {mcsTableData.name ?? t("Mcs.onPremBmpInfoModal.noData")}
                    </div>
                </div>
            </div>
            <Heading tag={"div"} variant="SUBTITLE_2" underline={false}>
                {t("Mcs.onPremBmpInfoModal.usageTitle")}
            </Heading>
            <div className={style.syncDataSection}>
                <div className={style.syncInfo}>
                    <div className={style.label}>{t("Mcs.onPremBmpInfoModal.reportCount")}</div>
                    <div data-testid={testIds.workArea.mcs.informationDialog.reportCount}>
                        {mcsTableData.reportCount ?? t("Common.na")}
                    </div>
                </div>

                {(mcsTableData.reportSyncEnabled === true ||
                    (mcsTableData.reportSynchronizedCount != null && mcsTableData.reportSynchronizedCount > 0)) && (
                    <div className={style.syncInfo}>
                        <div className={style.label}>{t("Mcs.onPremBmpInfoModal.reportSynchronizedCount")}</div>
                        <div data-testid={testIds.workArea.mcs.informationDialog.reportSynchronizedCount}>
                            {synchronizedCountDisplay}
                        </div>
                    </div>
                )}

                <div className={style.syncInfo}>
                    <div className={style.label}>{t("Mcs.onPremBmpInfoModal.workflowCount")}</div>
                    <div data-testid={testIds.workArea.mcs.informationDialog.workflowCount}>
                        {mcsTableData.workflowCount ?? t("Common.na")}
                    </div>
                </div>

                <div className={style.syncInfo}>
                    <div className={style.label}>{t("Mcs.onPremBmpInfoModal.userCount")}</div>
                    <div data-testid={testIds.workArea.mcs.informationDialog.userCount}>
                        {mcsTableData.userCount ?? t("Common.na")}
                    </div>
                </div>

                <div className={style.syncInfo}>
                    <div className={style.label}>{t("Mcs.onPremBmpInfoModal.containerId")}</div>
                    <div data-testid={testIds.workArea.mcs.informationDialog.containerId}>
                        {mcsTableData.containerId ?? t("Common.na")}
                    </div>
                </div>
                <div className={style.syncInfo}>
                    <div className={style.label}>{t("Mcs.onPremBmpInfoModal.entitlementState")}</div>
                    <div
                        className={style.statusBadge}
                        data-testid={testIds.workArea.mcs.informationDialog.entitlementState}
                    >
                        {mcsTableData.entitlementState ? (
                            <StatusBadge
                                values={[
                                    deriveEntitlementState(
                                        t,
                                        mcsTableData.entitlementState,
                                        mcsTableData.entitlementUpdateDate,
                                        true
                                    ),
                                ]}
                                tooltip={true}
                            />
                        ) : (
                            t("Common.na")
                        )}
                    </div>
                    <div
                        className={style.redirectIcon}
                        onClick={() => window.location.replace(ENTITLEMENT_LICENSES_ROUTE.path)}
                    >
                        <RedirectToView color={theme.iconFillColor} />
                    </div>
                </div>
            </div>

            <Heading tag="div" variant="SUBTITLE_2" underline={false}>
                {t("Mcs.onPremBmpInfoModal.licenseTitle")}
            </Heading>
            <div className={style.syncDataSection}>
                {mcsTableData.licenses && Object.entries(mcsTableData.licenses).length > 0 ? (
                    <div className={style.tableContainer}>
                        <StaticTable
                            testId={testIds.workArea.mcs.informationDialog.licensesTable.itself}
                            tableClass={style.table}
                            headers={[
                                {
                                    value: t("Mcs.onPremBmpInfoModal.licenseType"),
                                },
                                {
                                    value: t("Mcs.onPremBmpInfoModal.remainingLicense"),
                                },
                                {
                                    value: t("Mcs.onPremBmpInfoModal.expirationDate"),
                                },
                            ]}
                            cells={Object.entries(mcsTableData.licenses).map(([licenseId, license]) => {
                                const productName =
                                    blanccoProducts.find((type) => type.productId === licenseId)?.productName ||
                                    licenseId;

                                return [
                                    <div key={`name-${licenseId}`} className={style.label}>
                                        {productName}
                                    </div>,
                                    <div key={`remaining-${licenseId}`} className={style.label}>
                                        {license.remaining}
                                    </div>,
                                    <div key={`expiration-${licenseId}`} className={style.label}>
                                        {license.expiration && formatDateWithoutTime(license.expiration)}
                                    </div>,
                                ];
                            })}
                        />
                    </div>
                ) : (
                    <div>{t("Common.na")}</div>
                )}
            </div>
            <div className={classNames(style.syncDataOkButton, style.okButtonContainer)}>
                <button
                    className={buttons.primaryButtonWithoutIcon}
                    onClick={onClick}
                    data-testid={testIds.common.dialog.closeButton}
                >
                    {t("Common.ok")}
                </button>
            </div>
        </div>
    );
};

export default McSyncInformationView;
